import { useContext, type ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthContext, AuthUtils } from "@bfc-software/app-toolbox";
import { useNavigate } from "@tanstack/react-router";
import { EmptyState } from "@bfc-software/pallet";
import {
  ArrowRight,
  BarChart2,
  Home,
  LogOut,
  Package,
  Settings,
  Truck,
} from "react-feather";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import {
  availableTiles,
  externalTileUrl,
  isExternalTile,
  type InternalTileId,
  type Tile,
  type TileId,
} from "@bfc-software/bfc-web-ui/lib/tiles";
import { BrandedScreen } from "@bfc-software/bfc-web-ui/ui/shared/branded-screen";

import styles from "./index.module.css";

/**
 * The product picker - where a user lands after signing in, and the only screen
 * that knows about more than one product.
 *
 * It deliberately sits outside the app shell: no header, no navigation, no
 * warehouse. Each tile links to its product's own front door, and a product
 * that needs a warehouse asks for one there.
 */
export const Route = createFileRoute("/$lang/_layout/orgs/$org/_layout/")({
  component: RouteComponent,
});

const TILE_ICONS: Record<TileId, ReactNode> = {
  outbound: <Package size={30} strokeWidth={1.7} />,
  analytics: <BarChart2 size={30} strokeWidth={1.7} />,
  loadBuilder: <Truck size={30} strokeWidth={1.7} />,
  internal: <Settings size={30} strokeWidth={1.7} />,
};

/**
 * A lookup rather than a ternary, so adding a tile without a route is a compile
 * error instead of a link that quietly points at the wrong product. Products
 * hosted outside this app have no route and are keyed in the registry instead,
 * so both halves stay exhaustive.
 *
 * `satisfies` rather than an annotation: typing this as
 * Record<InternalTileId, string> widens the values, and Link stops checking
 * them against the route tree.
 */
const TILE_ROUTES = {
  outbound: "/$lang/orgs/$org/outbound",
  analytics: "/$lang/orgs/$org/analytics",
  internal: "/$lang/orgs/$org/internal",
} as const satisfies Record<InternalTileId, string>;

export function TileCard({
  tile,
  lang,
  org,
}: {
  tile: Tile;
  lang: string;
  org: string;
}) {
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.portal,
  });

  const { id } = tile;

  const body = (
    <>
      <span className={styles.tileIcon}>{TILE_ICONS[id]}</span>
      <span className={styles.tileTitle}>{t(`products.${id}.title`)}</span>
      <span className={styles.tileTagline}>{t(`products.${id}.tagline`)}</span>
      <span className={styles.tileDescription}>
        {t(`products.${id}.description`)}
      </span>
      <span className={styles.tileCta}>
        {tile.status === "open" ? t("tile.open") : t("tile.comingSoon")}
        {tile.status === "open" && <ArrowRight size={15} strokeWidth={2.2} />}
      </span>
    </>
  );

  // A product this app does not serve gets a plain anchor: a router link would
  // resolve against this app's route tree, and the destination is not in it.
  if (isExternalTile(id)) {
    return (
      <a
        href={externalTileUrl(id)}
        target="_blank"
        rel="noopener"
        className={styles.tile}
        data-tile={id}
      >
        {body}
      </a>
    );
  }

  // Every tile links to its product's own front door. Whether a product needs a
  // warehouse, and what to do when the organisation has none, is the product's
  // decision - the picker does not need to know.
  return (
    <Link
      to={TILE_ROUTES[id]}
      params={{ lang, org }}
      className={styles.tile}
      data-tile={id}
    >
      {body}
    </Link>
  );
}

function RouteComponent() {
  const params = Route.useParams();
  const navigate = useNavigate();
  const { state, logout } = useContext(AuthContext);
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.portal,
  });

  const orgId = AuthUtils.getOrgIdByStub(state, params.org);
  const orgName = orgId ? AuthUtils.getOrgName(state, orgId) : null;

  const tiles = availableTiles();

  return (
    <BrandedScreen>
      <h1 className={styles.heading}>{t("heading")}</h1>
      {orgName && (
        <p className={styles.company}>
          <Home size={15} strokeWidth={1.8} aria-hidden="true" />
          {orgName}
        </p>
      )}

      {tiles.length === 0 ? (
        <div className={styles.empty}>
          <EmptyState heading={t("empty.heading")}>
            {t("empty.body")}
          </EmptyState>
        </div>
      ) : (
        <ul className={styles.tiles}>
          {tiles.map((tile) => (
            <li key={tile.id} className={styles.tileItem}>
              <TileCard tile={tile} lang={params.lang} org={params.org} />
            </li>
          ))}
        </ul>
      )}

      {/* This screen has no shell header, so the way out lives here. */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.pill}
          onClick={() => {
            logout();
            navigate({
              to: "/$lang/auth/login",
              params: { lang: params.lang },
            });
          }}
        >
          <LogOut size={15} strokeWidth={2} aria-hidden="true" />
          {t("account.signOut")}
        </button>
      </div>
    </BrandedScreen>
  );
}
